import CodingalOffering from '@components/kids-teens-coding-camps/codingal-offering'
import Courses from '@components/kids-teens-coding-camps/courses'
import FirstFold from '@components/kids-teens-coding-camps/first-fold'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import Footer from '@components/common/footer'
import NavBar from '@components/common/navbar'
import Articles from '@components/home/articles'
import Faq from '@components/home/faq'
import MoneyBackGuarantee from '@components/home/guarantee'
import Teachers from '@components/home/teachers'
import Partners from '@components/locale-pages/home/us/partners'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { FAQPageJsonLd, NextSeo } from 'next-seo'
import { formatSeoConfig } from '@lib/seo'
import { defineMessage, Trans } from '@lingui/macro'
import { FaqContentType } from '@lib/data/faq'
import TextImageCommonFold from '@components/common/text-image-common-fold'
import { Investors } from '@components/book-trial-class-jp/investors'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import date from '@lib/utils/date.utils'
import Link from 'next/link'
import { msgT } from '@lib/utils/i18n'

const ThanksgivingCodingCampSeo = formatSeoConfig({
  title: `Thanksgiving Coding Camp for Kids & Teens | Codingal`,
  description: `Give your child a memorable Thanksgiving break with Codingal's online Thanksgiving Coding Camp. Build festive games, interactive stories, and AI projects while embracing gratitude and creativity. Limited seats—reserve today!`,
  url: 'https://www.codingal.com/kids-teens-coding-camps/thanksgiving/',
  metaImage: 'https://www.codingal.com/images/coding-camp/thanksgiving-og.jpg',
})

const couponCode = 'THANKS50'
const year = date().year()

export const FAQ_CONTENTS: FaqContentType[] = [
  {
    questionName: defineMessage({
      message: `What is the Thanksgiving Coding Camp ${year}?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal&apos;s Thanksgiving Coding Camp {year} is a five-day virtual
        experience where kids and teens build fall-themed games, apps, and
        stories. Each live session blends coding fundamentals with seasonal fun
        so learners stay engaged during the holiday break.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal's Thanksgiving Coding Camp ${year} is a five-day virtual experience where kids and teens build fall-themed games, apps, and stories. Each live session blends coding fundamentals with seasonal fun so learners stay engaged during the holiday break.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Which age groups can attend the Thanksgiving coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        The camp is designed for learners ages 7-18. We group students by school
        level - elementary, middle, and high school - so every child learns in a
        supportive environment that matches their pace and interests.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The camp is designed for learners ages 7-18. We group students by school level - elementary, middle, and high school - so every child learns in a supportive environment that matches their pace and interests.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is prior coding experience required to join the Thanksgiving coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        No. The Thanksgiving coding camp welcomes beginners and experienced
        coders alike. Instructors start with fundamentals and tailor challenges
        so every learner makes confident progress during the holiday week.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `No. The Thanksgiving coding camp welcomes beginners and experienced coders alike. Instructors start with fundamentals and tailor challenges so every learner makes confident progress during the holiday week.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What programming languages will participants learn at the Thanksgiving coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Learners explore Scratch, Python, Thunkable, Roblox Studio, Lua, HTML,
        CSS, JavaScript, and Data Science, depending on the track you select.
        Each project adds a festive Thanksgiving twist while building core
        coding skills.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Learners explore Scratch, Python, Thunkable, Roblox Studio, Lua, HTML, CSS, JavaScript, and Data Science, depending on the track you select. Each project adds a festive Thanksgiving twist while building core coding skills.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How are the Thanksgiving coding sessions conducted?`,
    }),
    acceptedAnswerText: (
      <>
        Live sessions are scheduled to leave room for family gatherings, and we
        share recordings after every lesson. Classes run on Codingal&apos;s
        interactive platform, where instructors guide learners in real time and
        encourage collaboration with peers.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Live sessions are scheduled to leave room for family gatherings, and we share recordings after every lesson. Classes run on Codingal's interactive platform, where instructors guide learners in real time and encourage collaboration with peers.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is the instructor-to-student ratio at the Thanksgiving coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        We maintain small group sizes - no more than four learners per class -
        so instructors can offer personalised feedback and support throughout
        each Thanksgiving session.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `We maintain small group sizes - no more than four learners per class - so instructors can offer personalised feedback and support throughout each Thanksgiving session.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What equipment or software does my child need for the Thanksgiving coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Learners need a computer or laptop with a reliable internet connection.
        Any required software is provided by Codingal at no extra cost, and our
        team helps families get set up before camp begins.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Learners need a computer or laptop with a reliable internet connection. Any required software is provided by Codingal at no extra cost, and our team helps families get set up before camp begins.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What projects will my child build during the Thanksgiving coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Kids and teens build gratitude journals in Scratch, autumn adventures in
        Roblox, AI-powered helpers in Python, and responsive web experiences
        that celebrate the season - all designed to reinforce real coding
        skills.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Kids and teens build gratitude journals in Scratch, autumn adventures in Roblox, AI-powered helpers in Python, and responsive web experiences that celebrate the season - all designed to reinforce real coding skills.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How do you track each learner's progress during camp?`,
    }),
    acceptedAnswerText: (
      <>
        Instructors monitor progress through daily activities, project reviews,
        and one-on-one feedback. We make sure every concept clicks before
        learners advance to the next Thanksgiving challenge.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Instructors monitor progress through daily activities, project reviews, and one-on-one feedback. We make sure every concept clicks before learners advance to the next Thanksgiving challenge.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is technical support available during the Thanksgiving coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Yes. Our support team is on standby before, during, and after every
        session to resolve technical issues quickly, keeping your child&apos;s
        Thanksgiving learning experience smooth.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. Our support team is on standby before, during, and after every session to resolve technical issues quickly, keeping your child's Thanksgiving learning experience smooth.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What additional support do families receive beyond the live Thanksgiving sessions?`,
    }),
    acceptedAnswerText: (
      <>
        Lifetime access to lesson materials, invitations to coding challenges
        and hackathons, unlimited doubt-clearing sessions, and 24/7 customer
        support to continue the learning momentum after camp ends.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Lifetime access to lesson materials, invitations to coding challenges and hackathons, unlimited doubt-clearing sessions, and 24/7 customer support to continue the learning momentum after camp ends.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How do I register for the Thanksgiving coding camp?`,
    }),
    acceptedAnswerText: (
      <>
        Choose the track that matches your child&apos;s interests, complete the
        simple online sign-up form, and use the coupon code to secure your seat.
        You&apos;ll receive a welcome kit with schedules, meeting links, and
        prerequisite guides.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Choose the track that matches your child's interests, complete the simple online sign-up form, and use the coupon code to secure your seat. You'll receive a welcome kit with schedules, meeting links, and prerequisite guides.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Is the Thanksgiving Coding Camp available online?`,
    }),
    acceptedAnswerText: (
      <>
        Yes. All Thanksgiving Coding Camp sessions are hosted live online with
        collaborative tools, so your child can participate from home while
        engaging with peers worldwide.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Yes. All Thanksgiving Coding Camp sessions are hosted live online with collaborative tools, so your child can participate from home while engaging with peers worldwide.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `What is Codingal's refund policy?`,
    }),
    acceptedAnswerText: (
      <>
        At Codingal, your satisfaction matters. If the Thanksgiving Coding Camp
        does not meet your expectations, you can cancel and receive a full
        refund. Email{' '}
        <a
          href="mailto:support@codingal.com"
          target="blank"
          rel="noreferrer"
          className="text-blue-200 underline"
        >
          support@codingal.com
        </a>{' '}
        to request a refund for any remaining classes - no questions asked.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `At Codingal, your satisfaction matters. If the Thanksgiving Coding Camp does not meet your expectations, you can cancel and receive a full refund. Email support@codingal.com to request a refund for any remaining classes - no questions asked.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `Why should I trust Codingal?`,
    }),
    acceptedAnswerText: (
      <>
        Codingal was founded by seasoned edtech entrepreneurs, IIT alumni, and
        former leaders from Google and Amazon, with backing from investors such
        as Y Combinator and Rebright Partners. Learn more about our story{' '}
        <Link href="/about-us" prefetch={false}>
          <a className="text-blue-200 underline">here</a>
        </Link>
        . We also stand behind every program with a money-back guarantee.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `Codingal was founded by seasoned edtech entrepreneurs, IIT alumni, and former leaders from Google and Amazon, with backing from investors such as Y Combinator and Rebright Partners. Learn more about our story here. We also stand behind every program with a money-back guarantee.`,
    }),
  },
  {
    questionName: defineMessage({
      message: `How did Codingal get its name?`,
    }),
    acceptedAnswerText: (
      <>
        The name &ldquo;Codingal&rdquo; blends &ldquo;coding&rdquo; and
        &ldquo;magical,&rdquo; capturing how we make learning feel engaging and
        inspiring for kids and teens.
      </>
    ),
    acceptedAnswerTextString: defineMessage({
      message: `The name "Codingal" blends "coding" and "magical," capturing how we make learning feel engaging and inspiring for kids and teens.`,
    }),
  },
]

const ThanksgivingCodingCampPage = () => {
  return (
    <>
      <NextSeo {...ThanksgivingCodingCampSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ_CONTENTS.map(
          ({ acceptedAnswerTextString, questionName }) => ({
            acceptedAnswerText:
              typeof acceptedAnswerTextString === 'string'
                ? acceptedAnswerTextString
                : msgT(acceptedAnswerTextString),
            questionName:
              typeof questionName === 'string'
                ? questionName
                : msgT(questionName),
          })
        )}
      />
      <NavBar isSticky />
      <FirstFold
        breadcrumbItems={[
          { name: 'Codingal', link: '/' },
          { name: 'Coding camps', link: '/kids-teens-coding-camps/' },
          {
            name: 'Thanksgiving camp',
            link: '/kids-teens-coding-camps/thanksgiving/',
          },
        ]}
        title={
          <>
            <Trans>Thanksgiving Coding Camp for Kids &amp; Teens</Trans> {year}
          </>
        }
        description={
          <>
            <p className="text-center lg:text-left text-sm lg:text-base">
              <Trans>
                🍂 Turn the holiday break into a celebration of creativity. Our
                Thanksgiving Coding Camp combines festive storytelling with real
                coding skills across Scratch, Python, Web &amp; App Development,
                and Roblox. Every live lesson is project-based, engaging, and
                packed with seasonal surprises.
              </Trans>
            </p>
            <p className="text-center lg:text-left pb-2 text-sm lg:text-base">
              <Trans>
                🦃 Students work with friendly expert instructors, collaborate
                with peers around the globe, and finish the week with a
                shareable portfolio. Seats are limited—reserve your child’s spot
                now and keep the Thanksgiving spirit of gratitude and growth
                alive.
              </Trans>
            </p>
          </>
        }
        couponCode={couponCode}
        coursePrice={149}
        discountedPrice={75}
        offerPercentage={50}
        imagePath="/images/coding-camp/thankgiving-first-fold-image"
        imageAlt="Celebrate Thanksgiving with Codingal's online coding camp"
        backgroundColour="radial-gradient(circle at 10% 20%, rgba(242, 163, 90, 0.4) 0%, rgba(242, 163, 90, 0) 55%), radial-gradient(circle at 90% 8%, rgba(255, 196, 140, 0.35) 0%, rgba(255, 196, 140, 0) 52%), linear-gradient(135deg, #46230f 0%, #6b3413 45%, #b85711 86%, #f2a65a 100%)"
        couponCodeTextBg="linear-gradient(135deg, #824820 0% 0%, #81451e 100%)"
      />
      <Partners />
      <Courses
        couponCode={couponCode}
        title={
          <Trans>
            Hand-picked Thanksgiving Coding Camps tailored to your child
          </Trans>
        }
      />
      <VideoTestimonial
        title="What parents and students say about our Thanksgiving coding camp"
        video_testimonial={VIDEO_TESTIMONIAL}
      />
      <TrustpilotReview isCampPage />
      <Teachers
        isShowBecomeTeacher={false}
        title="Learn coding from the best IT instructors"
        ctaLink="#courses"
        ctaText="Claim your spot now"
      />
      <CodingalOffering
        title={
          <Trans>Exclusive benefits of our Thanksgiving Coding Camp</Trans>
        }
      />
      <MoneyBackGuarantee
        isCodingCampPage
        title="Why parents choose Codingal's Thanksgiving coding camp"
      />
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 15%, rgba(255, 177, 109, 0.6) 0%, rgba(255, 177, 109, 0) 40%), radial-gradient(circle at 80% 80%, rgba(255, 227, 190, 0.65) 0%, rgba(255, 227, 190, 0) 50%), linear-gradient(135deg, #fff5eb 0%, #ffe0c7 60%, #ffd4ad 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                A Thanksgiving break filled with{' '}
                <span className="yellow-underline">
                  gratitude, games, and growth
                </span>
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    🍁 Inspired by the season of thanks, our curriculum weaves
                    storytelling and empathy into every coding challenge.
                    Learners curate gratitude journals, design fall-themed
                    adventures, and showcase their ideas with confidence.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    🧩 Project tracks are tuned for every age group, so younger
                    students explore block-based creativity while advanced
                    coders dive into Python automation and responsive web
                    design.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-activities"
            imageWidth={370}
            imageHeight={373}
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 85%, rgba(255, 215, 170, 0.7) 0%, rgba(255, 215, 170, 0) 40%), radial-gradient(circle at 80% 20%, rgba(240, 168, 92, 0.55) 0%, rgba(240, 168, 92, 0) 50%), linear-gradient(140deg, #ffe5cc 0%, #fcd6a8 55%, #f2b774 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                STEM.org Accredited{' '}
                <span className="yellow-underline">Educational Experience</span>{' '}
                and Certificates
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    🏅 Codingal&apos;s Thanksgiving Coding Camp continues our
                    STEM.org-accredited learning path, ensuring every workshop
                    meets trusted science, technology, engineering, and math
                    standards. Learners tackle gratitude-inspired coding
                    challenges that blend creativity with computational
                    thinking.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    📜 After camp, students receive an official
                    STEM.org-recognized certificate to showcase their seasonal
                    project portfolio. It&apos;s a powerful credential for
                    school applications, enrichment programs, and future STEM
                    opportunities.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-certificate"
            imageWidth={370}
            imageHeight={370}
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 15%, rgba(255, 201, 150, 0.7) 0%, rgba(255, 201, 150, 0) 40%), radial-gradient(circle at 80% 80%, rgba(255, 168, 107, 0.55) 0%, rgba(255, 168, 107, 0) 50%), linear-gradient(135deg, #fff3e4 0%, #ffd9b5 55%, #ffc799 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                Unlock your{' '}
                <span className="yellow-underline">
                  skills and earn coding certifications
                </span>
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    🎯 Keep learning momentum strong during the Thanksgiving
                    break with live, project-based sessions. Campers code
                    gratitude journals in Scratch, automate holiday to-do lists
                    in Python, and prototype responsive festive webpages with
                    HTML, CSS, and JavaScript—all guided by friendly experts.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    🪪 Graduates earn a STEM.org-accredited certification
                    alongside personalized feedback, proving their
                    problem-solving abilities and readiness for hackathons,
                    school clubs, and advanced Codingal courses.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-certificate-2"
            imageWidth={370}
            imageHeight={370}
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 85%, rgba(255, 202, 143, 0.7) 0%, rgba(255, 202, 143, 0) 40%), radial-gradient(circle at 80% 30%, rgba(255, 234, 200, 0.55) 0%, rgba(255, 234, 200, 0) 50%), linear-gradient(145deg, #ffe6ce 0%, #fbd1a8 58%, #f2af6d 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                The best Thanksgiving camp for{' '}
                <span className="yellow-underline">
                  kids to learn coding basics and fundamentals
                </span>
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    🧠 Our Thanksgiving Coding Camp delivers Codingal&apos;s
                    best-in-class online learning experience, helping beginners
                    and advanced learners lock in core concepts like logic,
                    loops, and game mechanics while celebrating the season of
                    gratitude.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    🛠️ Choose from curated tracks covering Scratch, Python, AI,
                    web development, and Roblox. Each program features six live
                    lessons that transform coding fundamentals into
                    portfolio-ready Thanksgiving projects.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    🌟 Families can reuse finished apps and games for school
                    showcases or community STEM fairs, extending the impact of
                    camp long after the holiday break.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-student-learning"
            imageWidth={370}
            imageHeight={373}
          />
        </div>
      </section>
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 20% 15%, rgba(255, 203, 152, 0.65) 0%, rgba(255, 203, 152, 0) 40%), radial-gradient(circle at 80% 70%, rgba(255, 236, 208, 0.55) 0%, rgba(255, 236, 208, 0) 50%), linear-gradient(140deg, #fff3e6 0%, #ffdcbf 55%, #ffc091 100%)',
        }}
      >
        <div className="relative">
          <TextImageCommonFold
            title={
              <>
                Our Thanksgiving courses are appropriate for{' '}
                <span className="yellow-underline">
                  high school, middle school,
                </span>{' '}
                and elementary school students
              </>
            }
            description={
              <>
                <p>
                  <Trans>
                    👩‍🎓 Thanksgiving cohorts are organized by age, so elementary,
                    middle, and high school students collaborate with peers who
                    share similar goals. The result is an encouraging space
                    where curious beginners and ambitious innovators both
                    thrive.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    🔭 Dedicated learning paths support first-time coders with
                    block-based storytelling while experienced learners explore
                    AI, data visualization, and app development. Daily progress
                    updates keep families engaged and connect the holiday
                    experience to long-term academic plans.
                  </Trans>
                </p>
              </>
            }
            imagePath="/images/coding-camp/summer-camp-school-kids"
            imageWidth={370}
            imageHeight={373}
          />
        </div>
      </section>
      <Faq
        title="Got questions? Find answers in our FAQ section"
        faqContents={FAQ_CONTENTS}
      />
      <Articles />
      <Investors isCodingCampPage />
      <Footer />
    </>
  )
}

export default ThanksgivingCodingCampPage
